import { LOCALE_STORAGE_KEYS } from 'common/constants/localeStorageConstants'
import { useAppSelector } from 'common/hooks/redux'
import { useDrawingHistory } from 'features/Procedure/hooks/useDrawingHistory'
import { useSceneActions } from 'features/Procedure/hooks/useSceneActions'
import type { BodyRef } from 'features/Procedure/procedure-model/body/body'
import type { CanvasCaptureRef } from 'features/Procedure/procedure-model/canvas-capture'
import {
  CustomizerColor,
  lineSizes,
  pinSizes,
} from 'features/Procedure/procedure-model/draw-customizer'

import {
  DEFAULT_MODEL_VERSION,
  DrawMode,
  EModelVersion,
} from 'features/Procedure/state/procedure.type'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { useLocalStorage } from 'react-use'
import type { Vector3Like } from 'three'

const TOGGLE_GENDER_MAP: Record<EModelVersion, EModelVersion> = {
  [EModelVersion.female_v1]: EModelVersion.male_v1,
  [EModelVersion.male_v1]: EModelVersion.female_v1,
  [EModelVersion.female_v2]: EModelVersion.male_v2,
  [EModelVersion.male_v2]: EModelVersion.female_v2,
}

const toggleGenderWithinFamily = (current: EModelVersion): EModelVersion =>
  TOGGLE_GENDER_MAP[current] ?? DEFAULT_MODEL_VERSION

export const useModelScene = () => {
  const location = useLocation()
  const { handleSetSelectedElement } = useSceneActions()
  const canvasCaptureRef = useRef<CanvasCaptureRef>(null)
  const bodyRef = useRef<BodyRef>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { handleSetDesiredCameraPosition, handleSetModelVersion } = useSceneActions()

  const drawMode = useAppSelector((state) => state.procedureReducer.drawMode)
  const selectedProcedure = useAppSelector((state) => state.procedureReducer.selectedProcedure)
  const scene = useAppSelector((state) => state.procedureReducer.scene)

  const { visibleElements, canUndo, canRedo, undo, redo } = useDrawingHistory(
    selectedProcedure?.id ?? null,
  )

  const [procedureColorMap, setProcedureColorMap] = useState<Record<string, CustomizerColor>>(
    () => {
      try {
        const raw = localStorage.getItem(LOCALE_STORAGE_KEYS.PROCEDURE_COLOR_MAP)
        return raw ? JSON.parse(raw) : {}
      } catch {
        return {}
      }
    },
  )

  const drawColor = useMemo(() => {
    if (!selectedProcedure) return CustomizerColor.Black
    return procedureColorMap[selectedProcedure.code] ?? CustomizerColor.Black
  }, [procedureColorMap, selectedProcedure])

  const handleColorChange = useCallback(
    (color: string) => {
      if (!selectedProcedure) return
      const procKey = selectedProcedure.code
      setProcedureColorMap((prev) => {
        const next = { ...prev, [procKey]: color as CustomizerColor }
        try {
          localStorage.setItem(LOCALE_STORAGE_KEYS.PROCEDURE_COLOR_MAP, JSON.stringify(next))
        } catch {
          // ignore write errors
        }
        return next
      })
    },
    [selectedProcedure],
  )

  const [pinWidth, setPinWidth] = useLocalStorage(LOCALE_STORAGE_KEYS.PIN_WIDTH, pinSizes[1])
  const [xWidth, setXWidth] = useLocalStorage(LOCALE_STORAGE_KEYS.X_WIDTH, pinSizes[1])
  const [lineWidth, setLineWidth] = useLocalStorage(LOCALE_STORAGE_KEYS.LINE_WIDTH, lineSizes[1])

  const isPin = useMemo(() => drawMode === DrawMode.PIN, [drawMode])
  const isX = useMemo(() => drawMode === DrawMode.X, [drawMode])

  const drawWidth = isPin ? pinWidth : isX ? xWidth : lineWidth

  const [isFullscreen, setIsFullscreen] = useState(false)
  const modelVersion = useAppSelector((state) => state.procedureReducer.modelVersion)

  const handleWidthChange = useCallback(
    (width: number) => {
      if (isPin) {
        setPinWidth(width)
      } else if (isX) {
        setXWidth(width)
      } else {
        setLineWidth(width)
      }
    },
    [isPin, isX, setPinWidth, setXWidth, setLineWidth],
  )

  useEffect(() => {
    const pinPresetValues = [pinSizes[0], pinSizes[1], pinSizes[2], pinSizes[3]]
    const closestPinPreset = (value: number) =>
      pinPresetValues.reduce((best, v) => (Math.abs(v - value) < Math.abs(best - value) ? v : best))

    if (isPin && pinWidth != null) {
      const matchesPreset = pinPresetValues.some((v) => Math.abs(pinWidth - v) < 1e-6)
      if (!matchesPreset) {
        setPinWidth(closestPinPreset(pinWidth))
      }
    } else if (isX && xWidth != null) {
      const matchesPreset = pinPresetValues.some((v) => Math.abs(xWidth - v) < 1e-6)
      if (!matchesPreset) {
        setXWidth(closestPinPreset(xWidth))
      }
    } else if (lineWidth != null) {
      const linePresetValues = [lineSizes[0], lineSizes[1], lineSizes[2], lineSizes[3]]
      const closestLinePreset = (value: number) =>
        linePresetValues.reduce((best, v) =>
          Math.abs(v - value) < Math.abs(best - value) ? v : best,
        )
      const matchesPreset = linePresetValues.some((v) => Math.abs(lineWidth - v) < 1e-6)
      if (!matchesPreset) {
        setLineWidth(closestLinePreset(lineWidth))
      }
    }
  }, [isPin, isX, pinWidth, xWidth, lineWidth, setPinWidth, setXWidth, setLineWidth])

  const handleCameraChange = useCallback(
    (position: Vector3Like, target: Vector3Like) => {
      handleSetDesiredCameraPosition({
        position: position,
        target: target,
      })
    },
    [handleSetDesiredCameraPosition],
  )

  const handleToggleFullscreen = useCallback(async () => {
    if (!containerRef.current) return

    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen()
        setIsFullscreen(true)
      } else {
        await document.exitFullscreen()
        setIsFullscreen(false)
      }
    } catch (error) {
      console.error('Error toggling fullscreen:', error)
    }
  }, [])

  useEffect(() => {
    handleSetSelectedElement(null)
  }, [location.pathname, location.search, handleSetSelectedElement])

  const handleToggleModel = useCallback(() => {
    const current = modelVersion ?? DEFAULT_MODEL_VERSION
    const nextVersion = toggleGenderWithinFamily(current)
    handleSetModelVersion(nextVersion)
  }, [modelVersion, handleSetModelVersion])

  const cameraPosition = useMemo(
    () => [scene.position.x, scene.position.y, scene.position.z] as [number, number, number],
    [scene.position.x, scene.position.y, scene.position.z],
  )

  const cameraTarget = useMemo(
    () => [scene.target.x, scene.target.y, scene.target.z] as [number, number, number],
    [scene.target.x, scene.target.y, scene.target.z],
  )

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  return {
    canvasCaptureRef,
    bodyRef,
    containerRef,
    drawMode,
    drawColor,
    drawWidth,
    isFullscreen,
    visibleElements,
    canUndo,
    canRedo,
    modelVersion,
    handleColorChange,
    handleWidthChange,
    handleCameraChange,
    handleToggleFullscreen,
    handleToggleModel,
    undo,
    redo,
    cameraPosition,
    cameraTarget,
  }
}
